/**
 * LEOS Mission Ready - Ground Station Upload UI (Refactored)
 * Main interface for uploading and managing ground station networks
 * Modular design with separate components for wizard, placement, and presets
 */

import { apiClient } from './api-integration.js';
import { GROUND_STATION_PRESETS } from './ground-station-presets.js';
import { GroundStationWizard } from './ground-station-wizard.js';
import { GroundStationPlacement } from './ground-station-placement.js';

export class GroundStationUploadUI {
    constructor() {
        this.isOpen = false;
        this.groundStations = [];
        this.wizardModule = new GroundStationWizard(this);
        this.placementModule = new GroundStationPlacement(this);
    }
    
    show() {
        if (this.isOpen) return;
        
        this.isOpen = true;
        
        // Create upload modal
        const modal = document.createElement('div');
        modal.className = 'ground-station-modal';
        modal.innerHTML = `
            <div class="station-panel">
                <div class="station-header">
                    <h2>Ground Station Network</h2>
                    <button class="close-btn" id="close-stations">×</button>
                </div>
                
                <div class="station-tabs">
                    <button class="tab-btn active" data-tab="upload">Upload Stations</button>
                    <button class="tab-btn" data-tab="manual">Manual Entry</button>
                    <button class="tab-btn" data-tab="network">Network Config</button>
                    <button class="tab-btn" data-tab="wizard">Network Wizard</button>
                    <button class="tab-btn" data-tab="strategic">Strategic Placement</button>
                </div>
                
                <div class="tab-content" id="upload-tab">
                    ${this.getUploadTabHTML()}
                </div>
                
                <div class="tab-content" id="manual-tab" style="display: none;">
                    ${this.getManualTabHTML()}
                </div>
                
                <div class="tab-content" id="network-tab" style="display: none;">
                    ${this.getNetworkTabHTML()}
                </div>
                
                <div class="tab-content" id="wizard-tab" style="display: none;">
                    <div class="upload-section">
                        <h3>Network Configuration Wizard</h3>
                        <p>Let our AI assistant help you design the optimal ground station network</p>
                        ${this.wizardModule.getWizardHTML()}
                    </div>
                </div>
                
                <div class="tab-content" id="strategic-tab" style="display: none;">
                    <div class="upload-section">
                        <h3>Strategic Station Placement</h3>
                        <p>Click on the globe to place ground stations at strategic locations</p>
                        ${this.placementModule.getPlacementHTML()}
                    </div>
                </div>
                
                <!-- Current Stations -->
                <div class="current-stations">
                    <h3>Current Ground Stations</h3>
                    <div id="stations-list" class="stations-grid">
                        <!-- Will be populated dynamically -->
                    </div>
                </div>
                
                <div id="station-status" class="station-status"></div>
            </div>
        `;
        
        // Add styles
        this.injectStyles();
        document.body.appendChild(modal);
        
        // Load current stations
        this.loadCurrentStations();
        
        // Attach event handlers
        this.attachEventHandlers(modal);
    }
    
    getUploadTabHTML() {
        return `
            <div class="upload-section">
                <h3>Upload Ground Station Data</h3>
                <p>Import ground station coordinates and configurations</p>
                
                <div class="format-selector">
                    <label>Data Format:</label>
                    <select id="station-format">
                        <option value="csv">CSV (Name, Lat, Lon, Alt, Type)</option>
                        <option value="json">JSON</option>
                        <option value="kml">KML/KMZ</option>
                        <option value="gpx">GPX</option>
                    </select>
                </div>
                
                <div class="file-upload-area">
                    <input type="file" id="station-file" accept=".csv,.json,.kml,.kmz,.gpx" style="display: none;">
                    <button class="upload-btn" onclick="document.getElementById('station-file').click()">
                        Choose File
                    </button>
                    <span id="station-file-name">No file selected</span>
                </div>
                
                <div class="or-divider">OR</div>
                
                <textarea id="station-text" placeholder="Paste data here...
Example CSV:
Name,Latitude,Longitude,Altitude,Type,Frequency
Canberra DSN,-35.4021,148.9819,688,Deep Space,2.3
Goldstone DSN,35.4050,-116.8904,1038,Deep Space,2.3
Madrid DSN,40.4275,-4.2486,837,Deep Space,2.3"></textarea>
                
                <button class="submit-btn" id="upload-stations">Upload Ground Stations</button>
            </div>
        `;
    }
    
    getManualTabHTML() {
        return `
            <div class="upload-section">
                <h3>Manual Ground Station Entry</h3>
                <form id="manual-station-form">
                    <div class="form-row">
                        <div class="form-group">
                            <label>Station Name *</label>
                            <input type="text" id="station-name" required placeholder="e.g., Kennedy Space Center" />
                        </div>
                        <div class="form-group">
                            <label>Station Type</label>
                            <select id="station-type">
                                <option value="tracking">Tracking Station</option>
                                <option value="dsn">Deep Space Network</option>
                                <option value="launch">Launch Complex</option>
                                <option value="mission">Mission Control</option>
                                <option value="observatory">Observatory</option>
                                <option value="radar">Radar Station</option>
                                <option value="amateur">Amateur Radio</option>
                                <option value="military">Military</option>
                            </select>
                        </div>
                    </div>
                    
                    <div class="form-row">
                        <div class="form-group">
                            <label>Latitude (°) *</label>
                            <input type="number" id="station-lat" required step="0.0001" min="-90" max="90" placeholder="28.5721" />
                        </div>
                        <div class="form-group">
                            <label>Longitude (°) *</label>
                            <input type="number" id="station-lon" required step="0.0001" min="-180" max="180" placeholder="-80.6480" />
                        </div>
                    </div>
                    
                    <div class="form-row">
                        <div class="form-group">
                            <label>Altitude (m)</label>
                            <input type="number" id="station-alt" step="1" min="0" placeholder="3" />
                        </div>
                        <div class="form-group">
                            <label>Frequency (GHz)</label>
                            <input type="number" id="station-freq" step="0.1" min="0" placeholder="2.3" />
                        </div>
                    </div>
                    
                    <div class="form-row">
                        <div class="form-group">
                            <label>Dish Diameter (m)</label>
                            <input type="number" id="station-diameter" step="0.1" min="0" placeholder="34" />
                        </div>
                        <div class="form-group">
                            <label>Coverage Range (km)</label>
                            <input type="number" id="station-range" step="1" min="0" placeholder="5000" />
                        </div>
                    </div>
                    
                    <div class="form-group">
                        <label>Description</label>
                        <textarea id="station-description" rows="3" placeholder="Station description and capabilities..."></textarea>
                    </div>
                    
                    <button type="submit" class="submit-btn">Add Ground Station</button>
                </form>
            </div>
        `;
    }
    
    getNetworkTabHTML() {
        return `
            <div class="upload-section">
                <h3>Network Configuration</h3>
                
                <div class="network-presets">
                    <h4>Quick Setup Presets</h4>
                    <div class="preset-buttons">
                        <button class="preset-btn" data-preset="dsn">NASA Deep Space Network</button>
                        <button class="preset-btn" data-preset="global">Global Tracking Network</button>
                        <button class="preset-btn" data-preset="military">Military Space Surveillance</button>
                        <button class="preset-btn" data-preset="amateur">Amateur Radio Network</button>
                        <button class="preset-btn" data-preset="commercial">Commercial Providers</button>
                    </div>
                </div>
                
                <div class="network-settings">
                    <h4>Network Settings</h4>
                    <div class="form-row">
                        <div class="form-group">
                            <label>Auto-LOS Calculation</label>
                            <select id="network-los">
                                <option value="enabled">Enabled - Show line-of-sight lines</option>
                                <option value="on-demand">On Demand - Show when clicked</option>
                                <option value="disabled">Disabled</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Coverage Circles</label>
                            <select id="network-coverage">
                                <option value="all">Show All Coverage Areas</option>
                                <option value="selected">Show Selected Only</option>
                                <option value="none">Hide Coverage Areas</option>
                            </select>
                        </div>
                    </div>
                    
                    <div class="form-row">
                        <div class="form-group">
                            <label>Minimum Elevation (°)</label>
                            <input type="number" id="network-min-elevation" value="10" min="0" max="90" />
                        </div>
                        <div class="form-group">
                            <label>Update Frequency (s)</label>
                            <input type="number" id="network-update-freq" value="5" min="1" max="300" />
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    
    attachEventHandlers(modal) {
        // Close button
        modal.querySelector('#close-stations').addEventListener('click', () => this.close());
        
        // Tab switching
        modal.querySelectorAll('.tab-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const tab = e.target.dataset.tab;
                this.switchTab(tab, modal);
            });
        });
        
        // File upload
        const fileInput = modal.querySelector('#station-file');
        fileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (file) {
                modal.querySelector('#station-file-name').textContent = file.name;
                this.readStationFile(file, modal);
            }
        });
        
        // Upload button
        modal.querySelector('#upload-stations').addEventListener('click', () => {
            this.uploadStations(modal);
        });
        
        // Manual form
        modal.querySelector('#manual-station-form').addEventListener('submit', (e) => {
            e.preventDefault();
            this.createManualStation(modal);
        });
        
        // Preset buttons
        modal.querySelectorAll('.preset-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const preset = e.target.dataset.preset;
                this.loadPreset(preset);
            });
        });
        
        // Attach wizard handlers
        this.wizardModule.attachEventHandlers(modal);
        
        // Attach placement handlers
        this.placementModule.attachEventHandlers(modal);
    }
    
    switchTab(tab, modal) {
        // Update tab buttons
        modal.querySelectorAll('.tab-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.tab === tab);
        });
        
        // Update content
        modal.querySelectorAll('.tab-content').forEach(content => {
            content.style.display = content.id === `${tab}-tab` ? 'block' : 'none';
        });
        
        // Reset wizard when switching away
        if (tab !== 'wizard') {
            this.wizardModule.reset();
        }
    }
    
    readStationFile(file, modal) {
        const reader = new FileReader();
        reader.onload = (e) => {
            modal.querySelector('#station-text').value = e.target.result;
        };
        reader.readAsText(file);
    }
    
    async uploadStations(modal) {
        const stationText = modal.querySelector('#station-text').value.trim();
        const format = modal.querySelector('#station-format').value;
        
        if (!stationText) {
            this.showStatus('Please paste station data or select a file', 'error');
            return;
        }
        
        this.showStatus('Processing ground stations...', 'loading');
        
        try {
            const stations = this.parseStationData(stationText, format);
            
            if (stations.length > 0) {
                // Upload to API
                for (const station of stations) {
                    await apiClient.createGroundStation(station);
                }
                
                this.showStatus(`Successfully uploaded ${stations.length} ground stations!`, 'success');
                this.loadCurrentStations();
                
                // Reload the scene to show new stations
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                this.showStatus('No valid stations found in data', 'error');
            }
        } catch (error) {
            this.showStatus(`Error: ${error.message}`, 'error');
        }
    }
    
    parseStationData(data, format) {
        const stations = [];
        
        switch (format) {
            case 'csv':
                const lines = data.trim().split('\n');
                const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
                
                for (let i = 1; i < lines.length; i++) {
                    const values = lines[i].split(',').map(v => v.trim());
                    
                    const station = {};
                    headers.forEach((header, index) => {
                        if (header.includes('name')) station.name = values[index];
                        else if (header.includes('lat')) station.latitude = parseFloat(values[index]);
                        else if (header.includes('lon')) station.longitude = parseFloat(values[index]);
                        else if (header.includes('alt')) station.altitude = parseFloat(values[index]) || 0;
                        else if (header.includes('type')) station.type = values[index];
                        else if (header.includes('freq')) station.frequency = parseFloat(values[index]);
                    });
                    
                    if (station.name && station.latitude && station.longitude) {
                        stations.push(station);
                    }
                }
                break;
                
            case 'json':
                const jsonData = JSON.parse(data);
                const stationArray = Array.isArray(jsonData) ? jsonData : [jsonData];
                stations.push(...stationArray);
                break;
                
            case 'kml':
                // Basic KML parsing (would need full XML parser for production)
                const placemarkRegex = /<Placemark>.*?<name>(.*?)<\/name>.*?<coordinates>(.*?)<\/coordinates>.*?<\/Placemark>/gs;
                let match;
                while ((match = placemarkRegex.exec(data)) !== null) {
                    const name = match[1];
                    const coords = match[2].split(',');
                    stations.push({
                        name: name,
                        longitude: parseFloat(coords[0]),
                        latitude: parseFloat(coords[1]),
                        altitude: parseFloat(coords[2]) || 0,
                        type: 'tracking'
                    });
                }
                break;
        }
        
        return stations;
    }
    
    async createManualStation(modal) {
        const name = modal.querySelector('#station-name').value.trim();
        const type = modal.querySelector('#station-type').value;
        const latitude = parseFloat(modal.querySelector('#station-lat').value);
        const longitude = parseFloat(modal.querySelector('#station-lon').value);
        const altitude = parseFloat(modal.querySelector('#station-alt').value) || 0;
        const frequency = parseFloat(modal.querySelector('#station-freq').value) || 2.3;
        const diameter = parseFloat(modal.querySelector('#station-diameter').value) || 34;
        const range = parseFloat(modal.querySelector('#station-range').value) || 5000;
        const description = modal.querySelector('#station-description').value.trim();
        
        if (!name || isNaN(latitude) || isNaN(longitude)) {
            this.showStatus('Name, latitude, and longitude are required', 'error');
            return;
        }
        
        this.showStatus('Creating ground station...', 'loading');
        
        try {
            const stationData = {
                name,
                type,
                latitude,
                longitude,
                altitude,
                frequency,
                diameter,
                range,
                description
            };
            
            await apiClient.createGroundStation(stationData);
            
            this.showStatus(`Successfully created ${name}!`, 'success');
            
            // Clear form
            modal.querySelector('#manual-station-form').reset();
            this.loadCurrentStations();
            
            // Reload the scene
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            this.showStatus(`Error: ${error.message}`, 'error');
        }
    }
    
    async loadPreset(preset) {
        this.showStatus('Loading preset...', 'loading');
        
        const stationData = GROUND_STATION_PRESETS[preset];
        if (!stationData) return;
        
        try {
            for (const station of stationData) {
                await apiClient.createGroundStation(station);
            }
            
            this.showStatus(`Loaded ${stationData.length} ${preset} stations`, 'success');
            this.loadCurrentStations();
        } catch (error) {
            this.showStatus(`Error loading preset: ${error.message}`, 'error');
        }
    }
    
    async loadCurrentStations() {
        try {
            const response = await apiClient.getGroundStations();
            this.groundStations = response.stations || [];
            this.updateStationsList();
        } catch (error) {
            console.log('Using default stations');
            this.groundStations = [
                { name: 'Canberra DSN', latitude: -35.4021, longitude: 148.9819, type: 'dsn' },
                { name: 'Vandenberg CA', latitude: 34.5658, longitude: -120.6228, type: 'launch' },
                { name: 'Wallops Island VA', latitude: 37.9402, longitude: -75.4668, type: 'launch' }
            ];
            this.updateStationsList();
        }
    }
    
    updateStationsList() {
        const container = document.querySelector('#stations-list');
        if (!container) return;
        
        if (this.groundStations.length === 0) {
            container.innerHTML = '<div style="color: #64748b; text-align: center; padding: 20px;">No ground stations configured</div>';
            return;
        }
        
        container.innerHTML = this.groundStations.map(station => `
            <div class="station-card">
                <h4>${station.name}</h4>
                <p>${station.type || 'tracking'} · ${station.latitude?.toFixed(4)}°, ${station.longitude?.toFixed(4)}°</p>
            </div>
        `).join('');
    }
    
    showStatus(message, type) {
        const status = document.querySelector('#station-status');
        status.textContent = message;
        status.className = `station-status ${type}`;
        status.style.display = 'block';
    }
    
    close() {
        const modal = document.querySelector('.ground-station-modal');
        if (modal) {
            modal.remove();
        }
        
        // Clean up placement module
        this.placementModule.cleanup();
        
        this.isOpen = false;
    }
    
    injectStyles() {
        // Check if styles already exist
        if (document.querySelector('#ground-station-styles')) return;
        
        const style = document.createElement('style');
        style.id = 'ground-station-styles';
        style.textContent = `
            ${this.getBaseStyles()}
            ${this.getWizardStyles()}
            ${this.getPlacementStyles()}
        `;
        
        document.head.appendChild(style);
    }
    
    getBaseStyles() {
        return `
            .ground-station-modal {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.8);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 10000;
            }
            
            .station-panel {
                background: #1e293b;
                border: 1px solid rgba(148, 163, 184, 0.2);
                border-radius: 16px;
                width: 90%;
                max-width: 900px;
                max-height: 90vh;
                overflow-y: auto;
                box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
            }
            
            .station-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 24px;
                border-bottom: 1px solid rgba(148, 163, 184, 0.2);
            }
            
            .station-header h2 {
                margin: 0;
                color: #e2e8f0;
                font-size: 24px;
            }
            
            .close-btn {
                background: none;
                border: none;
                color: #94a3b8;
                font-size: 28px;
                cursor: pointer;
                padding: 0;
                width: 32px;
                height: 32px;
            }
            
            .station-tabs {
                display: flex;
                padding: 0 24px;
                gap: 8px;
                margin-top: 20px;
                flex-wrap: wrap;
            }
            
            .tab-btn {
                background: rgba(15, 23, 42, 0.6);
                border: 1px solid rgba(148, 163, 184, 0.2);
                color: #94a3b8;
                padding: 10px 20px;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s;
            }
            
            .tab-btn.active {
                background: rgba(0, 153, 255, 0.2);
                border-color: rgba(0, 153, 255, 0.5);
                color: #00d4ff;
            }
            
            .tab-content {
                padding: 24px;
            }
            
            .upload-section h3 {
                color: #e2e8f0;
                margin: 0 0 8px 0;
            }
            
            .upload-section p {
                color: #94a3b8;
                margin: 0 0 20px 0;
            }
            
            .submit-btn {
                width: 100%;
                background: linear-gradient(45deg, #0099ff, #00d4ff);
                border: none;
                color: white;
                padding: 12px 24px;
                border-radius: 8px;
                font-size: 16px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.2s;
                margin-top: 20px;
            }
            
            .form-group {
                margin-bottom: 16px;
            }
            
            .form-group label {
                display: block;
                color: #94a3b8;
                margin-bottom: 6px;
                font-size: 14px;
            }
            
            .form-group input,
            .form-group select,
            .form-group textarea {
                width: 100%;
                background: rgba(15, 23, 42, 0.6);
                border: 1px solid rgba(148, 163, 184, 0.2);
                color: #e2e8f0;
                padding: 10px 12px;
                border-radius: 6px;
                font-size: 14px;
            }
            
            .form-row {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 16px;
            }
            
            .current-stations {
                margin-top: 32px;
                padding-top: 24px;
                border-top: 1px solid rgba(148, 163, 184, 0.2);
            }
            
            .stations-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                gap: 12px;
            }
            
            .station-card {
                background: rgba(15, 23, 42, 0.6);
                border: 1px solid rgba(148, 163, 184, 0.2);
                border-radius: 8px;
                padding: 16px;
                cursor: pointer;
                transition: all 0.2s;
            }
            
            .station-status {
                margin-top: 20px;
                padding: 12px;
                border-radius: 8px;
                display: none;
            }
            
            .station-status.success {
                background: rgba(16, 185, 129, 0.2);
                border: 1px solid rgba(16, 185, 129, 0.5);
                color: #10b981;
            }
            
            .station-status.error {
                background: rgba(239, 68, 68, 0.2);
                border: 1px solid rgba(239, 68, 68, 0.5);
                color: #ef4444;
            }
            
            .preset-buttons {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 12px;
            }
            
            .preset-btn {
                background: rgba(16, 185, 129, 0.2);
                border: 1px solid rgba(16, 185, 129, 0.3);
                color: #10b981;
                padding: 12px 16px;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s;
                font-size: 14px;
            }
        `;
    }
    
    getWizardStyles() {
        return `
            .wizard-step {
                min-height: 300px;
            }
            
            .mission-profiles {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 16px;
                margin-bottom: 24px;
            }
            
            .profile-option {
                display: block;
                cursor: pointer;
            }
            
            .profile-option input[type="radio"] {
                display: none;
            }
            
            .profile-card {
                background: rgba(15, 23, 42, 0.6);
                border: 2px solid rgba(148, 163, 184, 0.2);
                border-radius: 12px;
                padding: 20px;
                transition: all 0.3s;
            }
            
            .profile-option input[type="radio"]:checked + .profile-card {
                background: rgba(0, 153, 255, 0.1);
                border-color: rgba(0, 153, 255, 0.5);
                box-shadow: 0 0 20px rgba(0, 153, 255, 0.2);
            }
            
            .wizard-controls {
                display: flex;
                justify-content: space-between;
                margin-top: 32px;
                padding-top: 24px;
                border-top: 1px solid rgba(148, 163, 184, 0.2);
            }
            
            .wizard-btn {
                background: rgba(15, 23, 42, 0.6);
                border: 1px solid rgba(148, 163, 184, 0.2);
                color: #94a3b8;
                padding: 12px 24px;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s;
                font-size: 16px;
            }
            
            .wizard-btn.primary {
                background: rgba(0, 153, 255, 0.2);
                border-color: rgba(0, 153, 255, 0.5);
                color: #00d4ff;
            }
            
            .wizard-btn.success {
                background: rgba(16, 185, 129, 0.2);
                border-color: rgba(16, 185, 129, 0.5);
                color: #10b981;
            }
        `;
    }
    
    getPlacementStyles() {
        return `
            .placement-mode {
                display: flex;
                gap: 16px;
                margin: 20px 0;
                padding: 16px;
                background: rgba(15, 23, 42, 0.6);
                border: 1px solid rgba(148, 163, 184, 0.2);
                border-radius: 12px;
            }
            
            .mode-option {
                display: flex;
                align-items: center;
                color: #e2e8f0;
                cursor: pointer;
            }
            
            .placement-info {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 20px;
                margin: 20px 0;
            }
            
            .info-card {
                background: rgba(15, 23, 42, 0.6);
                border: 1px solid rgba(148, 163, 184, 0.2);
                border-radius: 12px;
                padding: 20px;
            }
            
            .placed-stations {
                margin-top: 32px;
                padding: 20px;
                background: rgba(15, 23, 42, 0.6);
                border: 1px solid rgba(148, 163, 184, 0.2);
                border-radius: 12px;
            }
            
            .placed-station-item {
                background: rgba(0, 153, 255, 0.1);
                border: 1px solid rgba(0, 153, 255, 0.3);
                border-radius: 8px;
                padding: 12px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;
            }
        `;
    }
}

// Create singleton instance
export const groundStationUploadUI = new GroundStationUploadUI();